import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, ShoppingCart, Trash2, Plus, Minus, ChevronRight, MapPin, Truck } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { resolveImageUrl, resolveImageUrlBusted } from '../utils/urlHelper';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import PriceDisplay from './PriceDisplay';
import useEscapeKey from '../hooks/useEscapeKey';

const GlobalCartDrawer = ({ show, onClose, rates }) => {
  useEscapeKey(onClose, show);
  const { 
    cart, 
    itemsByStore, 
    globalTotal, 
    totalItems, 
    updateQuantity, 
    removeFromCart, 
    clearCart,
    deliveryLocation, 
    deliveryQuotes,
    isQuoting 
  } = useCart();
  
  const navigate = useNavigate();

  // Helper for item unit prices (including modifiers)
  const getItemUnitPrice = (item) => {
    return item.unitPrice || item.price || 0;
  };

  return (
    <AnimatePresence>
      {show && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[1100]"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            className="fixed inset-y-0 right-0 w-full max-w-md bg-card border-l border-theme z-[1101] flex flex-col shadow-modal text-text"
          >
            <div className="p-6 border-b border-theme flex justify-between items-center bg-card/95 backdrop-blur-xl">
              <div>
                <h2 className="text-xl font-bold text-text">Mi Bolso</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className="flex h-2 w-2 rounded-full bg-primary animate-pulse"></span>
                  <p className="text-xs text-text-secondary">{totalItems} productos en total</p>
                </div>
              </div>
              <button 
                onClick={onClose} 
                className="p-2 rounded-full hover:bg-surface-2 transition-colors text-text-secondary hover:text-text"
                aria-label="Cerrar bolso"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-8 no-scrollbar">
              {itemsByStore.length > 0 ? (
                itemsByStore.map((storeGroup) => {
                  const storeDeliveryFee = deliveryQuotes[storeGroup.store_id];
                  
                  return (
                    <div key={storeGroup.store_id} className="space-y-4">
                      <div className="flex items-center gap-2 border-b border-theme pb-2">
                        <ShoppingCart size={14} className="text-primary" />
                        <h3 className="text-[11px] font-bold uppercase tracking-[0.15em] text-primary">
                          {storeGroup.store_name}
                        </h3>
                        <button 
                          onClick={() => {
                            onClose();
                            navigate(`/store/${storeGroup.store_slug}`);
                          }}
                          className="ml-auto text-[10px] bg-surface-2 hover:bg-surface-3 border border-theme px-2.5 py-1 rounded text-text-secondary hover:text-text transition-colors font-medium"
                        >
                          Ir a tienda
                        </button>
                      </div>

                      <div className="space-y-3">
                        {storeGroup.items.map((item) => (
                          <div key={item.signature} className="flex gap-4 group p-3 rounded-2xl bg-surface-1 border border-theme hover:border-slate-300 transition-all">
                            <div className="shrink-0">
                              <img
                                src={resolveImageUrlBusted(item.image_url, item.product_id) || resolveImageUrl(item.image_url)}
                                alt={item.name}
                                className="w-14 h-14 rounded-xl object-cover bg-card border border-theme"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-start gap-1">
                                    <h4 className="font-bold text-xs text-text truncate group-hover:text-primary transition-colors">{item.name}</h4>
                                    <div className="text-right shrink-0">
                                        <PriceDisplay 
                                            amountUsdt={item.totalPrice} 
                                            rates={rates}
                                            displayCurrencies={item.store_display_currencies || ['usdt']} 
                                            primaryClassName="text-xs font-black text-text"
                                            secondaryClassName="text-[10px] text-text-secondary"
                                        />
                                    </div>
                                </div>
                                <div className="flex items-center gap-4 mt-2">
                                    <div className="flex items-center gap-2 bg-card rounded-full p-1 border border-theme shadow-sm">
                                        <button
                                            onClick={() => updateQuantity(item.signature, -1)}
                                            className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-surface-2 transition-colors text-text-secondary hover:text-text"
                                            aria-label="Disminuir cantidad"
                                        >
                                            <Minus size={10} />
                                        </button>
                                        <span className="text-[10px] font-bold w-4 text-center text-text">{item.quantity}</span>
                                        <button
                                            onClick={() => updateQuantity(item.signature, 1)}
                                            className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-surface-2 transition-colors text-text-secondary hover:text-text"
                                            aria-label="Aumentar cantidad"
                                        >
                                            <Plus size={10} />
                                        </button>
                                    </div>
                                    <button 
                                        onClick={() => removeFromCart(item.signature)}
                                        className="text-[10px] text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 px-2 py-0.5 rounded transition-colors font-medium flex items-center gap-1"
                                    >
                                        <Trash2 size={11} />
                                        <span>Quitar</span>
                                    </button>
                                </div>
                            </div>
                          </div>
                        ))}
                        
                        {/* Store Delivery Row */}
                        {deliveryLocation && (
                          <div className="flex items-center justify-between p-3 rounded-xl bg-surface-2 border border-theme text-[10px]">
                            <div className="flex items-center gap-2 text-text-secondary">
                                <Truck size={12} className="text-primary" />
                                <span className="font-medium">Delivery {storeGroup.store_name}</span>
                            </div>
                            <div className="font-bold text-text">
                                {isQuoting ? '...' : (typeof storeDeliveryFee === 'number' ? `$${storeDeliveryFee.toFixed(2)}` : 'Error')}
                            </div>
                          </div>
                        )}

                        <div className="pt-1">
                          <button
                            type="button"
                            onClick={() => {
                              onClose();
                              navigate(`/store/${storeGroup.store_slug}?openCart=true`);
                            }}
                            className="w-full py-2.5 px-4 rounded-xl bg-primary/10 hover:bg-primary/20 border border-primary/30 text-primary text-xs font-bold transition-all flex items-center justify-between group"
                          >
                            <span>Comprar en {storeGroup.store_name}</span>
                            <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="h-[60vh] flex flex-col items-center justify-center text-center space-y-6">
                  <div className="relative">
                    <div className="w-20 h-20 bg-surface-2 rounded-3xl flex items-center justify-center border border-theme shadow-sm">
                      <ShoppingCart size={36} className="text-text-muted" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-text font-bold">Tu carrito está vacío</h3>
                    <p className="text-text-secondary text-xs max-w-[200px] mx-auto">
                      Agrega productos de cualquier tienda del Mercado.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate('/market');
                    }}
                    className="btn-primary"
                  >
                    Empezar a comprar
                    <ChevronRight size={14} className="inline ml-1" />
                  </button>
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div className="p-6 border-t border-theme bg-card shadow-modal space-y-4">
                <div className="flex justify-between items-end">
                  <div>
                    <span className="text-[11px] text-text-secondary uppercase font-bold tracking-wider block">Total en Bolso</span>
                  </div>
                  <div className="text-right font-black text-2xl text-primary">
                    ${globalTotal.toFixed(2)}
                  </div>
                </div>

                <div className="p-3 rounded-xl bg-surface-1 border border-theme text-[11px] text-text-secondary leading-relaxed text-center">
                  Los pagos en MundoXYZ se procesan de forma individual por tienda. Selecciona una tienda arriba para continuar con el proceso de pago.
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default GlobalCartDrawer;
